import React, { useState, useMemo, useCallback, useRef } from 'react';

export const FASES_PREDETERMINADAS = [
    { key: 'no_laborable', label: 'Días no laborables', color: '#9CA3AF', responsable: '-' },
    { key: 'divulgacion', label: 'Divulgación', color: '#F472B6', responsable: 'Gestión Humana' },
    { key: 'solicitud', label: 'Solicitud de pedido', color: '#FBBF24', responsable: 'Personal' },
    { key: 'consolidado', label: 'Consolidado de pedidos', color: '#065F46', responsable: 'Costos' },
    { key: 'digitacion', label: 'Digitación de pedido', color: '#DC2626', responsable: 'Comercial' },
    { key: 'preparacion', label: 'Preparación de pedido', color: '#2563EB', responsable: 'Supply Chain' },
    { key: 'retiro', label: 'Retiro del Pedido', color: '#06B6D4', responsable: 'Personal' },
    { key: 'credito', label: 'Días de Crédito', color: '#10B981', responsable: 'Personal' },
    { key: 'tope_pago', label: 'Fecha tope de pago', color: '#1E3A5F', responsable: 'Personal' },
    { key: 'conciliacion', label: 'Conciliación de pago', color: '#7C3AED', responsable: 'Tesorería' },
    { key: 'aprobacion', label: 'Aprobación siguiente proceso', color: '#84CC16', responsable: 'Finanzas' },
];

const DIAS_SEMANA = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * CalendarioFases - Paint-style phase calendar
 *
 * Props:
 *   diasPintados: { "YYYY-MM-DD": "phase_key", ... }  — which days are assigned to which phase
 *   onChangeDias: (newDias) => void  — callback when days change (edit mode only)
 *   editable: boolean — if true, allows painting days
 *   mesesMostrar: [{ year, month }, ...]  — which months to render (optional, auto-detected from data)
 */
const CalendarioFases = ({ diasPintados = {}, onChangeDias, editable = false, mesesMostrar }) => {
    const [selectedFase, setSelectedFase] = useState(null); // key of the active "brush"
    const [isPainting, setIsPainting] = useState(false);
    const paintingRef = useRef(false);

    // Determine months to render
    const mesesARender = useMemo(() => {
        if (mesesMostrar && mesesMostrar.length > 0) return mesesMostrar;

        const dates = Object.keys(diasPintados);
        if (dates.length === 0) {
            // Default: current month + next 2
            const now = new Date();
            return [
                { year: now.getFullYear(), month: now.getMonth() },
                { year: now.getFullYear() + (now.getMonth() + 1 > 11 ? 1 : 0), month: (now.getMonth() + 1) % 12 },
                { year: now.getFullYear() + (now.getMonth() + 2 > 11 ? 1 : 0), month: (now.getMonth() + 2) % 12 },
            ];
        }

        let minDate = null;
        let maxDate = null;
        dates.forEach(d => {
            const dt = new Date(d + 'T00:00:00');
            if (!minDate || dt < minDate) minDate = dt;
            if (!maxDate || dt > maxDate) maxDate = dt;
        });

        const meses = [];
        const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        const last = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
        while (current <= last) {
            meses.push({ year: current.getFullYear(), month: current.getMonth() });
            current.setMonth(current.getMonth() + 1);
        }
        return meses;
    }, [diasPintados, mesesMostrar]);

    const getFaseForDate = useCallback((dateStr) => {
        const key = diasPintados[dateStr];
        if (!key) return null;
        const def = FASES_PREDETERMINADAS.find(f => f.key === key);
        return def || null;
    }, [diasPintados]);

    const paintDay = useCallback((dateStr) => {
        if (!editable || !onChangeDias) return;
        const newDias = { ...diasPintados };

        if (selectedFase === null) {
            // Eraser mode
            delete newDias[dateStr];
        } else if (diasPintados[dateStr] === selectedFase) {
            // Toggle off
            delete newDias[dateStr];
        } else {
            newDias[dateStr] = selectedFase;
        }
        onChangeDias(newDias);
    }, [editable, onChangeDias, diasPintados, selectedFase]);

    const handleMouseDown = useCallback((dateStr) => {
        if (!editable) return;
        paintingRef.current = true;
        setIsPainting(true);
        paintDay(dateStr);
    }, [editable, paintDay]);

    const handleMouseEnter = useCallback((dateStr) => {
        if (!editable || !paintingRef.current) return;
        // While dragging, always paint (don't toggle)
        if (!onChangeDias) return;
        const newDias = { ...diasPintados };
        if (selectedFase === null) {
            delete newDias[dateStr];
        } else {
            newDias[dateStr] = selectedFase;
        }
        onChangeDias(newDias);
    }, [editable, diasPintados, selectedFase, onChangeDias]);

    const handleMouseUp = useCallback(() => {
        paintingRef.current = false;
        setIsPainting(false);
    }, []);

    // Compute which phases are used (for legend in view mode)
    const fasesUsadas = useMemo(() => {
        const keys = new Set(Object.values(diasPintados));
        return FASES_PREDETERMINADAS.filter(f => keys.has(f.key));
    }, [diasPintados]);

    const renderMonth = (year, month) => {
        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDow = firstDay.getDay();

        const cells = [];
        for (let i = 0; i < startDow; i++) {
            cells.push(<td key={`empty-${i}`} className="p-0" />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const fase = getFaseForDate(dateStr);

            cells.push(
                <td
                    key={day}
                    className={`text-center text-sm font-medium p-0 ${editable ? 'cursor-pointer select-none' : ''}`}
                    style={{
                        backgroundColor: fase ? fase.color : 'transparent',
                        color: fase ? '#fff' : '#6B7280',
                        textShadow: fase ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                        height: '32px',
                        width: '40px',
                        border: '1px solid #e5e7eb',
                    }}
                    title={fase ? fase.label : ''}
                    onMouseDown={() => editable && handleMouseDown(dateStr)}
                    onMouseEnter={() => editable && handleMouseEnter(dateStr)}
                    onMouseUp={() => editable && handleMouseUp()}
                >
                    {day}
                </td>
            );
        }

        // Build rows of 7 cells each
        const rows = [];
        let row = [];
        cells.forEach((cell, i) => {
            row.push(cell);
            if (row.length === 7) {
                rows.push(<tr key={`row-${rows.length}`}>{row}</tr>);
                row = [];
            }
        });
        // Fill last row if incomplete
        if (row.length > 0) {
            while (row.length < 7) {
                row.push(<td key={`pad-${row.length}`} className="p-0" style={{ height: '32px', width: '40px', border: '1px solid #e5e7eb' }} />);
            }
            rows.push(<tr key={`row-${rows.length}`}>{row}</tr>);
        }

        return (
            <div key={`${year}-${month}`}>
                <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}
                    onMouseLeave={() => { if (editable) { paintingRef.current = false; setIsPainting(false); } }}
                >
                    <thead>
                        <tr>
                            <th colSpan="7" className="text-center text-base font-bold text-gray-800 py-2 bg-gray-50 border border-gray-200 uppercase">
                                {MESES[month]} {year}
                            </th>
                        </tr>
                        <tr>
                            {DIAS_SEMANA.map(d => (
                                <th key={d} className="text-center text-xs font-bold text-gray-600 py-1.5 bg-gray-50 border border-gray-200" style={{ width: '40px' }}>
                                    {d}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {/* Phase palette (editable mode only) */}
            {editable && (
                <div className="mb-6">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Selecciona una fase para pintar</h4>
                    <div className="flex flex-wrap gap-2">
                        {/* Eraser */}
                        <button
                            onClick={() => setSelectedFase(null)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${selectedFase === null ? 'border-gray-900 bg-white shadow-md ring-2 ring-gray-300' : 'border-gray-200 bg-white hover:border-gray-400'}`}
                        >
                            <div className="w-4 h-4 rounded bg-white border-2 border-gray-300 flex items-center justify-center text-[10px]">✕</div>
                            Borrar
                        </button>
                        {FASES_PREDETERMINADAS.map(fase => (
                            <button
                                key={fase.key}
                                onClick={() => setSelectedFase(fase.key)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${selectedFase === fase.key ? 'border-gray-900 shadow-md ring-2 ring-gray-300' : 'border-gray-200 hover:border-gray-400'}`}
                                title={`${fase.label} (${fase.responsable})`}
                            >
                                <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: fase.color }} />
                                <span className="hidden lg:inline text-gray-700">{fase.label}</span>
                            </button>
                        ))}
                    </div>
                    {selectedFase && (
                        <p className="text-sm text-gray-500 mt-2">
                            Haz clic o arrastra sobre los días del calendario para pintarlos con <strong>{FASES_PREDETERMINADAS.find(f => f.key === selectedFase)?.label}</strong>
                        </p>
                    )}
                    {selectedFase === null && (
                        <p className="text-sm text-gray-500 mt-2">
                            Modo borrador: haz clic en un día para quitar su fase asignada.
                        </p>
                    )}
                </div>
            )}

            {/* Calendar grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {mesesARender.map(({ year, month }) => renderMonth(year, month))}
            </div>

            {/* Legend table */}
            {(editable || fasesUsadas.length > 0) && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="w-10 px-3 py-2"></th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600">Descripción</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600">Responsable</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {(editable ? FASES_PREDETERMINADAS : fasesUsadas).map(fase => (
                                <tr key={fase.key} className="hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                        <div className="w-5 h-4 rounded" style={{ backgroundColor: fase.color }} />
                                    </td>
                                    <td className="px-3 py-2 text-gray-800 font-medium">{fase.label}</td>
                                    <td className="px-3 py-2 text-gray-500">{fase.responsable}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Empty state for view mode */}
            {!editable && fasesUsadas.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                    <p className="text-lg font-medium">No hay fechas de fases configuradas</p>
                    <p className="text-sm mt-1">El calendario aparecerá cuando se configuren las fechas del proceso.</p>
                </div>
            )}
        </div>
    );
};

export default CalendarioFases;
